import { RefObject, useCallback, useState } from "react";
import { Body } from "@sprout/typography";

import { TextInput } from "@sprout/text_input";
import TrashIcon from "@sprout/icons/trash";
import { IconButton } from "@sprout/icon_button";
import { useMonthOptions } from "@hooks/use_month_options";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount";
import useTranslation from "@hooks/use_translation";
import { AsyncSingleSelect, SingleSelect } from "@sprout/select";
import { useLoadAsyncOptions } from "@sprout/select/hooks";
import type { Option } from "@sprout/select";
import { TertiaryButton } from "@sprout/buttons";

export type Education = {
  school?: Option;
  degree?: Option;
  discipline?: Option;
  schoolId?: number;
  degreeId?: number;
  disciplineId?: number;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
  key: string;
};

type EducationSectionProps = {
  educations: Education[];
  setEducations: (educations: Education[]) => void;
  educationsError: { [key: number]: string };
  educationPath: string;
  educationRefs: RefObject<HTMLElement[]>;
};

type EducationProps = {
  education: Education;
  setEducation: (education: Education) => void;
  showDelete?: boolean;
  deleteEducation: () => void;
  error?: string;
  index: number;
  educationPath: string;
  educationRefs: RefObject<HTMLElement[]>;
};

const Education = ({
  education,
  setEducation,
  showDelete,
  deleteEducation,
  error,
  index,
  educationPath,
  educationRefs,
}: EducationProps) => {
  const months = useMonthOptions();
  const { t } = useTranslation("user");

  const [school, setSchool] = useState(education.school || null);
  const [degree, setDegree] = useState(education.degree || null);
  const [discipline, setDiscipline] = useState(education.discipline || null);
  const [startMonth, setStartMonth] = useState(
    months.find((m) => m.value === education.startMonth) || null,
  );
  const [startYear, setStartYear] = useState(education.startYear);
  const [endMonth, setEndMonth] = useState(
    months.find((m) => m.value === education.endMonth) || null,
  );
  const [endYear, setEndYear] = useState(education.endYear);

  const loadSchoolOptions = useLoadAsyncOptions(`${educationPath}/schools`);
  const loadDegreeOptions = useLoadAsyncOptions(`${educationPath}/degrees`);
  const loadDisciplineOptions = useLoadAsyncOptions(`${educationPath}/disciplines`);

  useEffectAfterMount(() => {
    setEducation({
      school: school || undefined,
      degree: degree || undefined,
      discipline: discipline || undefined,
      startMonth: startMonth?.value as number,
      startYear,
      endMonth: endMonth?.value as number,
      endYear,
      key: education.key,
    });
  }, [school, degree, discipline, startMonth, startYear, endMonth, endYear]);

  const setDivRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      educationRefs.current[index] = node;
    }
  }, []);

  return (
    <div className="form" ref={setDivRef}>
      {index > 0 && <hr />}
      <div className="section-header">
        <Body medium>{t("education.header")}</Body>
        {showDelete && (
          <IconButton
            label={t("education.remove")}
            size="md"
            icon={TrashIcon}
            onClick={deleteEducation}
          />
        )}
      </div>
      <AsyncSingleSelect
        onChange={setSchool}
        value={school}
        label={t("education.school")}
        loadOptions={loadSchoolOptions}
        clearable
        creatable
      />
      <AsyncSingleSelect
        onChange={setDegree}
        value={degree}
        label={t("education.degree")}
        loadOptions={loadDegreeOptions}
        clearable
        creatable
      />
      <AsyncSingleSelect
        onChange={setDiscipline}
        value={discipline}
        label={t("education.discipline")}
        loadOptions={loadDisciplineOptions}
        clearable
        creatable
      />
      {/* Start dates */}
      <div className="dates">
        <SingleSelect
          onChange={setStartMonth}
          value={startMonth}
          label={t("dates.startDateMonth")}
          options={months}
          clearable
        />
        <TextInput
          id="startYear"
          type="number"
          label={t("dates.startDateYear")}
          onChange={(event) => setStartYear(parseInt(event.currentTarget.value))}
          maxLength={4}
          value={startYear?.toString() || ""}
          error={error}
        />
      </div>

      {/* End dates */}
      <div className="dates">
        <SingleSelect
          label={t("dates.endDateMonth")}
          options={months}
          onChange={setEndMonth}
          value={endMonth}
          clearable
        />
        <TextInput
          id="endYear"
          type="number"
          label={t("dates.endDateYear")}
          onChange={(event) => setEndYear(parseInt(event.currentTarget.value))}
          maxLength={4}
          value={endYear?.toString() || ""}
        />
      </div>
    </div>
  );
};

const EducationSection = ({
  educations,
  setEducations,
  educationsError,
  educationPath,
  educationRefs,
}: EducationSectionProps) => {
  const addEducation = () => {
    setEducations([...educations, { key: crypto.randomUUID() }]);
  };
  const { t } = useTranslation("user");

  return (
    <div className="quick_apply__education" id="education">
      {educations.map((education, index) => {
        return (
          <Education
            key={education.key}
            education={education}
            setEducation={(updatedEducation) => {
              const updatedEducations = [...educations];
              updatedEducations[index] = updatedEducation;
              setEducations(updatedEducations);
            }}
            showDelete={educations.length > 1}
            deleteEducation={() => {
              const updatedEducations = [...educations];
              updatedEducations.splice(index, 1);
              setEducations(updatedEducations);
            }}
            error={educationsError[index]}
            index={index}
            educationPath={educationPath}
            educationRefs={educationRefs}
          />
        );
      })}
      <TertiaryButton onClick={addEducation}>{t("add_school")}</TertiaryButton>
    </div>
  );
};

export default EducationSection;
